import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from 'shared/layouts/Main'
import { Button, Form } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import InputWrapper from 'shared/components/InputWrapper'
import resAddCustomer from './resolvers/resAddCustomer'
import useInit from './hooks/useInit'
import useFocusError from 'shared/hooks/useFocusError'
import prepareSubmitForm from 'shared/utils/prepareSubmitForm'
import submitForm from './utils/submitForm'
import getBrandContent from 'utils/getBrandContent'
import Footer from 'shared/components/Footer'
import BackButton from 'shared/components/BackButton'
import Editor from 'ckeditor5-custom-build/build/ckeditor'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import ModalEditableProps from 'components/EditModal/types/ModalEditableProps'
import prepareSubmitFormFromModal from 'components/EditModal/utils/prepareSubmitFormFromModal'

const submitRef = React.createRef<HTMLButtonElement>()

const AddCustomer: React.FunctionComponent<ModalEditableProps> = React.forwardRef(
  (props: ModalEditableProps, ref) => {
    const [showOverlay, setShowOverlay] = useState<boolean>(true)
    const [dataLoaded, setDataLoaded] = useState<boolean>(false)
    const [defaultHomeUrl, setDefaultHomeUrl] = useState<string>('')
    const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})
    const [inactiveMessage, setInactiveMessage] = useState<string>('')

    const { id } = useParams()

    const navigate = useNavigate()

    const [customerId] = useState<string | undefined>(props.modalConfig?.entityId ?? id)

    const [colAttributes] = useState<any>(props.modalConfig !== undefined ? { md: 12 } : undefined)

    const {
      register,
      getValues,
      handleSubmit,
      reset,
      setFocus,
      setError,
      setValue,
      formState: { errors },
    } = useForm({
      mode: 'all',
      resolver: resAddCustomer,
    })

    useInit({
      getValues,
      id: customerId,
      reset,
      setDataLoaded,
      setInactiveMessage,
      setShowOverlay,
      currentCustomer,
      setDefaultHomeUrl,
    })

    useFocusError({
      errors,
      setFocus,
    })

    const renderView = (innerContent: any) => {
      return props.modalConfig === undefined ? (
        <Layout
          footerContent={<Footer />}
          brandContent={getBrandContent()}
          showOverlay={showOverlay}
          showCardHeader={true}
          headerInsert={<BackButton url='/customers' text='All Customers' />}
          actionInsert={
            <Button
              disabled={!dataLoaded}
              type='submit'
              name='save'
              className='btn-round btn-primary'
              onClick={() => {
                if (submitRef.current != null) {
                  submitRef.current.click()
                }
              }}
            >
              Save Customer
            </Button>
          }
        >
          {innerContent}
        </Layout>
      ) : (
        innerContent
      )
    }

    return renderView(
      <Form
        onSubmit={handleSubmit((data) => {
          const commonParams = {
            submitForm,
            data,
            setShowOverlay,
            setError,
            id: customerId,
            name: 'Customer',
            successNav: '/customers',
          }

          return props.modalConfig !== undefined
            ? prepareSubmitFormFromModal({
                ...commonParams,
                modalConfig: props.modalConfig,
              })
            : prepareSubmitForm({
                ...commonParams,
                navigate: navigate,
              })
        })}
        id='main-form'
      >
        <InputWrapper colAttributes={colAttributes} label='Name' name='name' errors={errors}>
          <Form.Control type='text' {...register('name')} />
        </InputWrapper>

        <InputWrapper colAttributes={colAttributes} label='Visualize ID' name='vslz_id' errors={errors}>
          <Form.Control type='text' {...register('vslz_id')} />
        </InputWrapper>

        <InputWrapper colAttributes={colAttributes} label='Display Name' name='display_name' errors={errors}>
          <Form.Control type='text' {...register('display_name')} />
        </InputWrapper>

        <InputWrapper colAttributes={colAttributes} label='Home URL' name='home_url' errors={errors}>
          <Form.Control type='text' {...register('home_url')} />

          <Form.Text muted>
            {`Leave this field empty if you want to use default value (${defaultHomeUrl}).`}
          </Form.Text>
        </InputWrapper>

        <InputWrapper colAttributes={colAttributes} label='Visualize Password' name='vslz_password' errors={errors}>
          <Form.Control type='text' {...register('vslz_password')} />
        </InputWrapper>

        <InputWrapper colAttributes={colAttributes} label='AIS Security String' name='ais_security_string' errors={errors}>
          <Form.Control type='text' {...register('ais_security_string')} />
        </InputWrapper>

        {dataLoaded && (
          <InputWrapper name='active' errors={errors} colAttributes={{ md: 12 }}>
            <Form.Check
              id='chb-active'
              type='checkbox'
              value='1'
              className='checkbox'
              {...register('active')}
            />
            <label htmlFor='chb-active'>Active</label>
          </InputWrapper>
        )}

        <InputWrapper
          label='Inactive Message'
          name='inactive_message'
          errors={errors}
          colAttributes={{ md: 12 }}
        >
          <CKEditor
            editor={Editor}
            data={inactiveMessage}
            onChange={(event: any, editor: any) => {
              const data = editor.getData()
              setInactiveMessage(data)
              setValue('inactive_message', data)
            }}
          />
        </InputWrapper>

        <Button
          type='submit'
          name='save'
          ref={(ref as any) ?? submitRef}
          className='d-none'
        ></Button>
      </Form>,
    )
  },
)

export default AddCustomer
